import React, { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Smartphone, Share, Plus, X, CheckCircle2, MoreVertical } from 'lucide-react';
import { toast } from 'sonner';

const esIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
const esAndroid = () => /Android/.test(navigator.userAgent);
const esStandalone = () =>
  window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true;

export default function InstalarAppCard() {
  const [deferred, setDeferred] = useState(null);
  const [instalada, setInstalada] = useState(esStandalone());
  const [mostrarIOS, setMostrarIOS] = useState(false);
  const [mostrarAndroid, setMostrarAndroid] = useState(false);
  const [ios] = useState(esIOS());
  const [android] = useState(esAndroid());

  useEffect(() => {
    const handler = (e) => { e.preventDefault(); setDeferred(e); };
    const onInstalled = () => { setInstalada(true); setDeferred(null); setMostrarAndroid(false); toast.success('¡App instalada!'); };
    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const instalar = async () => {
    if (deferred) {
      deferred.prompt();
      const { outcome } = await deferred.userChoice;
      if (outcome === 'accepted') setInstalada(true);
      setDeferred(null);
    } else if (ios) {
      setMostrarIOS(true);
    } else if (android) {
      setMostrarAndroid(true);
    } else {
      toast.info('Abre el menú de tu navegador y elige "Instalar app" o "Añadir a pantalla de inicio".');
    }
  };

  if (instalada) {
    return (
      <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/10 px-4 py-3" data-testid="empapp-instalar-card-ok">
        <div className="flex items-center gap-3">
          <CheckCircle2 className="h-5 w-5 text-emerald-400 shrink-0" />
          <div className="flex-1">
            <p className="font-bold text-sm text-emerald-100 leading-tight">App instalada</p>
            <p className="text-xs text-emerald-200/80 mt-0.5">Tienes acceso directo desde tu pantalla de inicio.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="rounded-xl border border-indigo-500/40 bg-indigo-500/10 px-4 py-3" data-testid="empapp-instalar-card">
        <div className="flex items-start gap-3 mb-2.5">
          <Smartphone className="h-5 w-5 text-indigo-300 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-bold text-sm text-indigo-100 leading-tight">Instalar en el móvil</p>
            <p className="text-xs text-indigo-200/80 mt-0.5">Ánclala en tu pantalla de inicio para abrirla como una app normal.</p>
          </div>
        </div>
        <Button size="sm" onClick={instalar} data-testid="empapp-instalar-btn"
          className="w-full bg-indigo-500 hover:bg-indigo-600 h-9 text-xs font-semibold">
          <Plus className="h-4 w-4 mr-1.5" /> {ios ? 'Ver cómo instalarla' : 'Instalar app'}
        </Button>
      </div>

      {mostrarIOS && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" onClick={() => setMostrarIOS(false)}>
          <div className="w-full max-w-sm bg-slate-900 border border-slate-700 rounded-2xl p-6 relative" onClick={(e) => e.stopPropagation()} data-testid="empapp-instalar-ios">
            <button onClick={() => setMostrarIOS(false)} className="absolute top-3 right-3 text-slate-400 hover:text-white" data-testid="empapp-instalar-ios-close"><X className="h-5 w-5" /></button>
            <h3 className="font-heading text-lg font-bold text-white mb-1">Instalar en iPhone / iPad</h3>
            <p className="text-xs text-slate-400 mb-4">Sigue estos pasos en Safari:</p>
            <ol className="space-y-3 text-sm text-slate-200">
              <li className="flex items-start gap-3">
                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-indigo-500/30 text-indigo-300 text-xs font-bold shrink-0">1</span>
                <span>Toca el botón <b>Compartir</b> <Share className="inline h-4 w-4 mx-0.5 text-indigo-300" /> en la barra de Safari.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-indigo-500/30 text-indigo-300 text-xs font-bold shrink-0">2</span>
                <span>Baja hasta <b>Añadir a pantalla de inicio</b>.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-indigo-500/30 text-indigo-300 text-xs font-bold shrink-0">3</span>
                <span>Toca <b>Añadir</b> arriba a la derecha. Listo, tendrás el icono en tu inicio.</span>
              </li>
            </ol>
          </div>
        </div>
      )}

      {mostrarAndroid && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" onClick={() => setMostrarAndroid(false)}>
          <div className="w-full max-w-sm bg-slate-900 border border-slate-700 rounded-2xl p-6 relative" onClick={(e) => e.stopPropagation()} data-testid="empapp-instalar-android">
            <button onClick={() => setMostrarAndroid(false)} className="absolute top-3 right-3 text-slate-400 hover:text-white" data-testid="empapp-instalar-android-close"><X className="h-5 w-5" /></button>
            <h3 className="font-heading text-lg font-bold text-white mb-1">Instalar en Android</h3>
            <p className="text-xs text-slate-400 mb-4">Chrome aún no ofrece la instalación directa. Se activará después de usar la app un rato, o puedes hacerlo manualmente:</p>
            <ol className="space-y-3 text-sm text-slate-200">
              <li className="flex items-start gap-3">
                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-indigo-500/30 text-indigo-300 text-xs font-bold shrink-0">1</span>
                <span>Toca los <b>tres puntos</b> <MoreVertical className="inline h-4 w-4 mx-0.5 text-indigo-300" /> arriba a la derecha en Chrome.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-indigo-500/30 text-indigo-300 text-xs font-bold shrink-0">2</span>
                <span>Pulsa <b>Instalar app</b> (o <b>Añadir a pantalla de inicio</b>).</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-indigo-500/30 text-indigo-300 text-xs font-bold shrink-0">3</span>
                <span>Confirma con <b>Instalar</b>. Listo, tendrás el icono en tu inicio.</span>
              </li>
            </ol>
            <p className="text-[11px] text-slate-500 mt-4">Si vuelves a esta app un par de veces más, Chrome ofrecerá el botón directo automáticamente.</p>
          </div>
        </div>
      )}
    </>
  );
}
